/**
 * Walkthrough Provider
 *
 * Provides tour functionality across the application using driver.js.
 * Manages tour state, cross-page navigation, and lazy-loads the driver.js library.
 */

import React, { createContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Driver } from 'driver.js';

// Tour definitions
import { ddSettingsTour } from './tours/ddSettingsTour';
import { ddOperationalTour } from './tours/ddOperationalTour';
import { dropzoneTour } from './tours/dropzoneTour';
import { fileLabelsTour } from './tours/fileLabelsTour';
import { voorzieningenTour } from './tours/voorzieningenTour';
import { fullDDWorkflowTour } from './tours/fullDDWorkflowTour';
import { ddConditionsTour } from './tours/ddConditionsTour';
import { customFieldsTour } from './tours/customFieldsTour';
import { leegstandTour } from './tours/leegstandTour';
import type { TourDefinition } from './config/tourSteps';

const TOUR_CONTINUATION_KEY = 'yallyhub_tour_continuation';

interface TourContinuation {
  nextPageUrl: string;
  nextTourId: string;
}

interface WalkthroughContextValue {
  /** Start a tour by ID */
  startTour: (tourId: string) => Promise<void>;
  /** Stop the current tour */
  stopTour: () => void;
  /** Whether a tour is currently active */
  isActive: boolean;
  /** ID of the currently active tour */
  currentTourId: string | null;
}

export const WalkthroughContext = createContext<WalkthroughContextValue | null>(null);

interface WalkthroughProviderProps {
  children: React.ReactNode;
}

// Map of all available tours
const TOURS: Record<string, TourDefinition> = {
  ddSettingsTour,
  ddOperationalTour,
  dropzoneTour,
  fileLabelsTour,
  voorzieningenTour,
  fullDDWorkflowTour,
  ddConditionsTour,
  customFieldsTour,
  leegstandTour,
};

export const WalkthroughProvider: React.FC<WalkthroughProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const [isActive, setIsActive] = useState(false);
  const [currentTourId, setCurrentTourId] = useState<string | null>(null);
  const [driverInstance, setDriverInstance] = useState<Driver | null>(null);

  /**
   * Lazy load driver.js and its CSS
   */
  const loadDriver = useCallback(async () => {
    try {
      const { driver } = await import('driver.js');
      await import('driver.js/dist/driver.css');
      return driver;
    } catch (error) {
      console.error('Error loading driver.js:', error);
      throw error;
    }
  }, []);

  /**
   * Check for tour continuation on mount
   * If a tour was started on another page and navigated here, continue it
   */
  useEffect(() => {
    const checkContinuation = async () => {
      try {
        const continuationData = sessionStorage.getItem(TOUR_CONTINUATION_KEY);
        if (continuationData) {
          const { nextTourId } = JSON.parse(continuationData) as TourContinuation;
          sessionStorage.removeItem(TOUR_CONTINUATION_KEY);

          // Small delay to ensure page is fully rendered
          setTimeout(() => {
            startTour(nextTourId);
          }, 500);
        }
      } catch (error) {
        console.error('Error checking tour continuation:', error);
      }
    };

    checkContinuation();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Start a tour by ID
   */
  const startTour = useCallback(
    async (tourId: string) => {
      try {
        const tourDefinition = TOURS[tourId];
        if (!tourDefinition) {
          console.error(`Tour with ID "${tourId}" not found`);
          return;
        }

        // Load driver.js if not already loaded
        const driverFn = await loadDriver();

        // Create driver instance
        const instance = driverFn({
          showProgress: true,
          showButtons: ['next', 'previous', 'close'],
          nextBtnText: 'Volgende',
          prevBtnText: 'Vorige',
          doneBtnText: 'Klaar',
          progressText: '{{current}} van {{total}}',
          steps: tourDefinition.steps.map((step) => ({
            element: step.element,
            popover: {
              title: step.popover.title,
              description: step.popover.description,
              side: step.popover.side || 'bottom',
              align: step.popover.align || 'start',
            },
          })),
          onDestroyed: () => {
            setIsActive(false);
            setCurrentTourId(null);
            setDriverInstance(null);

            // Handle cross-page navigation
            if (tourDefinition.nextPageUrl && tourDefinition.nextTourId) {
              const continuation: TourContinuation = {
                nextPageUrl: tourDefinition.nextPageUrl,
                nextTourId: tourDefinition.nextTourId,
              };
              sessionStorage.setItem(TOUR_CONTINUATION_KEY, JSON.stringify(continuation));
              navigate(tourDefinition.nextPageUrl);
            }
          },
        });

        setDriverInstance(instance);
        setCurrentTourId(tourId);
        setIsActive(true);

        instance.drive();
      } catch (error) {
        console.error('Error starting tour:', error);
        setIsActive(false);
        setCurrentTourId(null);
      }
    },
    [loadDriver, navigate]
  );

  /**
   * Stop the current tour
   */
  const stopTour = useCallback(() => {
    if (driverInstance) {
      driverInstance.destroy();
    }
    setIsActive(false);
    setCurrentTourId(null);
    setDriverInstance(null);
  }, [driverInstance]);

  const contextValue = useMemo(
    () => ({
      startTour,
      stopTour,
      isActive,
      currentTourId,
    }),
    [startTour, stopTour, isActive, currentTourId]
  );

  return <WalkthroughContext.Provider value={contextValue}>{children}</WalkthroughContext.Provider>;
};
